import { ArrowRightIcon, SparksAIIcon } from "@cpj-ai/panel-npm-package";
import { Button } from "@heroui/react";
import { useVoiceVisualizer, VoiceVisualizer } from "react-voice-visualizer";
import AiAnalysisProgress from "./AiAnalysisProgress";
import { useAiJobPositionCreator } from "./hooks/useAiJobPositionCreator";
import JobFieldsList from "./JobFieldsList";
import AiJobPositionCreatorLayout from "./layout";

interface IAudioJobCreatorProps {
  handleNextStep: () => void;
  setSelectedMethod: (method: "voice" | "file" | null) => void;
}

const AudioJobCreator = ({ handleNextStep, setSelectedMethod }: IAudioJobCreatorProps) => {
  const recorderControls = useVoiceVisualizer();
  const {
    recordedBlob,
    isRecordingInProgress,
    isPausedRecording,
    isAvailableRecordedAudio,
    startRecording,
    togglePauseResume,
    stopRecording,
    clearCanvas,
    startAudioPlayback,
    stopAudioPlayback,
    isPausedRecordedAudio,
  } = recorderControls;

  const { handleAnalyzeWithAI, isAnalyzing } = useAiJobPositionCreator({ handleNextStep });

  return (
    <AiJobPositionCreatorLayout description="Simply describe your job post requirements by voice. Our AI will analyze your recording and automatically fill in the job details for you. This is designed for your convenience, but you can always skip and create a job post manually if you prefer.">
      {isAnalyzing ? (
        <AiAnalysisProgress source="audio" />
      ) : (
        <>
          <div className="h-[200px]">
            <VoiceVisualizer
              controls={recorderControls}
              height={200}
              mainBarColor="#0070F3"
              secondaryBarColor="#7EB6FF"
              backgroundColor="#F5F5F5"
              isControlPanelShown={false}
              canvasContainerClassName="rounded-3xl"
            />
          </div>
          <JobFieldsList />

          <div className="mb-3 mt-6">
            <div className="flex flex-col sm:flex-row gap-2 mb-3 -mt-4">
              {!isRecordingInProgress && !isAvailableRecordedAudio && (
                <Button color="primary" onPress={startRecording} fullWidth>
                  Start Recording
                </Button>
              )}

              {isRecordingInProgress && (
                <>
                  <Button variant="bordered" color="primary" onPress={togglePauseResume} fullWidth>
                    {isPausedRecording ? "Resume" : "Pause"}
                  </Button>
                  <Button color="primary" onPress={stopRecording} fullWidth>
                    Stop Recording
                  </Button>
                </>
              )}

              {isAvailableRecordedAudio && (
                <div className="flex items-center flex-col gap-2 w-full">
                  <Button
                    color="primary"
                    className="w-fit min-w-0 py-6 px-2"
                    radius="full"
                    onPress={isPausedRecordedAudio ? startAudioPlayback : stopAudioPlayback}
                  >
                    {isPausedRecordedAudio ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                        <g
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        >
                          <path d="m14.752 11.168l-3.197-2.132A1 1 0 0 0 10 9.87v4.263a1 1 0 0 0 1.555.832l3.197-2.132a1 1 0 0 0 0-1.664" />
                          <path d="M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0" />
                        </g>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 9v6m4-6v6m7-3a9 9 0 1 1-18 0a9 9 0 0 1 18 0"
                        />
                      </svg>
                    )}
                  </Button>
                  <div className="flex gap-2 w-full mt-4">
                    <Button
                      color="primary"
                      startContent={<SparksAIIcon className="w-6 h-6 stroke-white" />}
                      onPress={() => recordedBlob && handleAnalyzeWithAI(recordedBlob)}
                      isDisabled={isAnalyzing}
                      fullWidth
                    >
                      Analyze with AI
                    </Button>
                    <Button variant="bordered" color="primary" onPress={clearCanvas} fullWidth>
                      Record Again
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="bordered"
              color="primary"
              className="w-full"
              startContent={<ArrowRightIcon className="w-6 h-6 rotate-180 mb-1.5 -mr-1 stroke-primary-500" />}
              onPress={() => setSelectedMethod(null)}
            >
              Change Method
            </Button>
          </div>
        </>
      )}
    </AiJobPositionCreatorLayout>
  );
};

export default AudioJobCreator;
